<template>
  <div class="store-category-container">
    <h1 class="page-title">设施分类管理</h1>
    <el-card class="content-card">
      <div class="card-header">
        <el-button type="primary" @click="handleAdd">新增分类</el-button>
      </div>
      <div class="card-content">
        <el-table :data="categories" border stripe style="width: 100%" v-loading="loading">
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="name" label="分类名称" />
          <el-table-column prop="description" label="分类描述" width="300" show-overflow-tooltip />

          <el-table-column prop="status" label="状态" width="100">
            <template #default="scope">
              <el-tag :type="scope.row.status ? 'success' : 'danger'">
                {{ scope.row.status ? '启用' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="show_in_travel" label="是否在旅行板块展示" width="180">
            <template #default="scope">
              <el-switch 
                :model-value="scope.row.show_in_travel" 
                @change="(value) => handleShowInTravelChange(scope.row, value)"
                :active-value="true"
                :inactive-value="false"
              />
            </template>
          </el-table-column>
          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <el-button type="primary" size="small" @click="handleEdit(scope.row)">
                编辑
              </el-button>
              <el-button 
                type="danger" 
                size="small" 
                @click="handleDelete(scope.row)"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>
    
    <!-- 新增/编辑分类抽屉 -->
    <el-drawer
      v-model="dialogVisible"
      :title="isEdit ? '编辑分类' : '新增分类'"
      direction="rtl"
      size="40%"
    >
      <el-form :model="form" ref="formRef" :rules="rules" label-width="120px">
        <el-form-item label="分类名称" prop="name">
          <el-input v-model="form.name" placeholder="请输入分类名称" />
        </el-form-item>
        <el-form-item label="分类描述" prop="description">
          <el-input 
            v-model="form.description" 
            type="textarea" 
            :rows="3" 
            placeholder="请输入分类描述"
          />
        </el-form-item>

        <el-form-item label="状态" prop="status">
          <el-switch v-model="form.status" />
        </el-form-item>
        
        <el-form-item label="旅行板块展示" prop="show_in_travel">
          <el-switch v-model="form.show_in_travel" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit">确定</el-button>
        </span>
      </template>
    </el-drawer>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '../../api/axios';

// 分类列表数据
const categories = ref([]);
// loading状态
const loading = ref(false);
// 对话框显示状态
const dialogVisible = ref(false);
// 表单引用
const formRef = ref();
// 是否为编辑模式
const isEdit = ref(false);

// 表单数据
const form = reactive({
  id: '',
  name: '',
  description: '',
  status: true,
  show_in_travel: false
});

// 表单验证规则 - 使用普通对象
// eslint-disable-next-line no-unused-vars
const rules = {
  name: [
    { required: true, message: '请输入分类名称', trigger: 'blur' },
    { max: 50, message: '分类名称不能超过50个字符', trigger: 'blur' }
  ],
  description: [
    { max: 255, message: '分类描述不能超过255个字符', trigger: 'blur' }
  ],
  status: [
    { type: 'boolean', message: '状态必须是布尔值', trigger: 'change' }
  ],
  show_in_travel: [
    { type: 'boolean', message: '是否在旅行板块展示必须是布尔值', trigger: 'change' }
  ]
};

// 获取分类列表
const fetchCategories = async () => {
  loading.value = true;
  try {
    const response = await request({
      url: '/manage/store-categories',
      method: 'get'
    });
    // 检查响应数据结构，分类列表在response.data.list中
    if (response && response.data && response.data.list) {
      // 确保show_in_travel字段为布尔类型
      categories.value = response.data.list.map(item => ({
        ...item,
        show_in_travel: !!item.show_in_travel,
        status: !!item.status
      }));
    } else {
      categories.value = [];
    }
  } catch (error) {
    ElMessage.error('获取分类列表失败');
  } finally {
    loading.value = false;
  }
};

// 页面加载时获取分类列表
onMounted(() => {
  fetchCategories();
});

// 新增分类
// eslint-disable-next-line no-unused-vars
const handleAdd = () => {
  isEdit.value = false;
  // 重置表单
  Object.assign(form, {
    id: '',
    name: '',
    description: '',
    status: true,
    show_in_travel: false
  });
  dialogVisible.value = true;
};

// 编辑分类
// eslint-disable-next-line no-unused-vars
const handleEdit = (row) => {
  isEdit.value = true;
  // 填充表单数据，确保布尔类型字段正确转换
  Object.assign(form, {
    id: row.id,
    name: row.name,
    description: row.description,
    status: !!row.status, // 确保转换为布尔类型
    show_in_travel: !!row.show_in_travel // 确保转换为布尔类型
  });
  dialogVisible.value = true;
};

// 删除分类
// eslint-disable-next-line no-unused-vars
const handleDelete = (row) => {
  ElMessageBox.confirm('确定要删除该分类吗？', '警告', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      await request({
        url: `/manage/store-categories/${row.id}`,
        method: 'delete'
      });
      ElMessage.success('删除分类成功');
      fetchCategories();
    } catch (error) {
      ElMessage.error('删除分类失败');
      console.error(error);
    }
  }).catch(() => {
    // 取消删除操作
  });
};

// 处理是否在旅行板块展示开关变化
// eslint-disable-next-line no-unused-vars
const handleShowInTravelChange = async (row, value) => {
  try {
    // 先更新本地状态
    row.show_in_travel = value;
    
    await request({
      url: `/manage/store-categories/${row.id}`,
      method: 'put',
      data: {
        ...row,
        show_in_travel: value
      }
    });
    ElMessage.success('更新成功');
  } catch (error) {
    ElMessage.error('更新失败');
    // 恢复原始状态
    fetchCategories();
    console.error(error);
  }
};

// 提交表单
// eslint-disable-next-line no-unused-vars
const handleSubmit = async () => {
  if (!formRef.value) return;
  
  try {
    await formRef.value.validate();
    
    if (isEdit.value) {
      // 更新分类
      await request({
        url: `/manage/store-categories/${form.id}`,
        method: 'put',
        data: form
      });
      ElMessage.success('更新分类成功');
    } else {
      // 新增分类
      await request({
        url: '/manage/store-categories',
        method: 'post',
        data: form
      });
      ElMessage.success('新增分类成功');
    }
    
    dialogVisible.value = false;
    fetchCategories();
  } catch (error) {
    if (error.name === 'Error') {
      // 表单验证失败，不显示错误信息
      return;
    }
    ElMessage.error(isEdit.value ? '更新分类失败' : '新增分类失败');
    console.error(error);
  }
};
</script>

<style scoped>
.store-category-container {
  padding: 20px;
}

.page-title {
  font-size: 24px;
  margin-bottom: 20px;
  color: #333;
}

.content-card {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.card-header h2 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.card-content {
  padding: 20px 0;
}
</style>